// Remap the equirectangular globe bathymetry (−180…180) into the Pacific-
// centered world window (20→380) used by swell map / globe UVs, then optionally
// crop to a basin bbox. Cached so toggling Bathy doesn’t re-decode every time.

const SRC = `${process.env.PUBLIC_URL || ''}/globe-bathy.jpg`;
const WORLD = { lonMin: 20, lonMax: 380, latMin: -75, latMax: 75 };

let worldBlobUrl = null;
let worldPromise = null;

function loadImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`bathy load failed: ${url}`));
    img.src = url;
  });
}

function remapPacific(src, { lonMin, lonMax, latMin, latMax }, outW = 2048) {
  const sw = src.naturalWidth || src.width;
  const sh = src.naturalHeight || src.height;
  const lonSpan = lonMax - lonMin || 1;
  const latSpan = latMax - latMin || 1;
  const dw = outW;
  const dh = Math.max(2, Math.round((dw * latSpan) / lonSpan));
  const cv = document.createElement('canvas');
  cv.width = dw;
  cv.height = dh;
  const ctx = cv.getContext('2d');
  const tmp = document.createElement('canvas');
  tmp.width = sw;
  tmp.height = sh;
  const tctx = tmp.getContext('2d');
  tctx.drawImage(src, 0, 0);
  const srcData = tctx.getImageData(0, 0, sw, sh).data;
  const img = ctx.createImageData(dw, dh);
  const dst = img.data;
  for (let y = 0; y < dh; y += 1) {
    const lat = latMax - (y / (dh - 1)) * latSpan;
    const sy = Math.min(sh - 1, Math.max(0, Math.round(((90 - lat) / 180) * (sh - 1))));
    for (let x = 0; x < dw; x += 1) {
      let lon = lonMin + (x / (dw - 1)) * lonSpan;
      lon = ((((lon + 180) % 360) + 360) % 360) - 180;
      const sx = Math.min(sw - 1, Math.max(0, Math.round(((lon + 180) / 360) * (sw - 1))));
      const si = (sy * sw + sx) * 4;
      const di = (y * dw + x) * 4;
      dst[di] = srcData[si];
      dst[di + 1] = srcData[si + 1];
      dst[di + 2] = srcData[si + 2];
      dst[di + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);
  return cv;
}

/** Pacific-window world bathy as an object URL (cached). */
export function loadWorldPacificBathyUrl() {
  if (worldBlobUrl) return Promise.resolve(worldBlobUrl);
  if (worldPromise) return worldPromise;
  worldPromise = loadImage(SRC)
    .then((img) => {
      const cv = remapPacific(img, WORLD);
      return new Promise((resolve) => {
        cv.toBlob((blob) => {
          if (!blob) {
            worldPromise = null;
            resolve('');
            return;
          }
          worldBlobUrl = URL.createObjectURL(blob);
          resolve(worldBlobUrl);
        }, 'image/jpeg', 0.82);
      });
    })
    .catch((err) => {
      worldPromise = null;
      throw err;
    });
  return worldPromise;
}

/**
 * CSS position for cropping the Pacific-window world mosaic into a basin bbox
 * (same math as Live sat on SwellMap).
 */
export function basinCropStyle(doc, world = WORLD) {
  if (!doc || ![doc.lonMin, doc.lonMax, doc.latMin, doc.latMax].every(Number.isFinite)) {
    return undefined;
  }
  const sw = world.lonMax - world.lonMin;
  const sh = world.latMax - world.latMin;
  const left = (doc.lonMin - world.lonMin) / sw;
  const right = (doc.lonMax - world.lonMin) / sw;
  const top = (world.latMax - doc.latMax) / sh;
  const bottom = (world.latMax - doc.latMin) / sh;
  const wFrac = Math.max(0.02, right - left);
  const hFrac = Math.max(0.02, bottom - top);
  if (wFrac > 0.98 && hFrac > 0.98) return undefined;
  return {
    position: 'absolute',
    width: `${100 / wFrac}%`,
    height: `${100 / hFrac}%`,
    left: `${(-left / wFrac) * 100}%`,
    top: `${(-top / hFrac) * 100}%`,
    maxWidth: 'none',
    objectFit: 'fill',
  };
}
